import { supabase } from '@/lib/supabase';

export interface DiscountInfo {
  code: string;
  amountOff?: number; // in cents
  percentOff?: number; // percentage (e.g., 20 for 20%)
  description?: string;
  promotionCodeId: string;
}

export interface DiscountValidationResult {
  isValid: boolean;
  discount?: DiscountInfo;
  error?: string;
}

// Cart item structure for validation (simplified)
export interface CartItemForValidation {
  type: string; // 'tournament', 'addon', 'event_registration', etc.
  itemId: string;
}

// In-memory cache for validated discount codes
// Key: discount code (lowercase), Value: { discount info, timestamp }
const discountCache = new Map<string, { discount: DiscountInfo; timestamp: number }>();

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

/**
 * Validate a discount code with Stripe API via Supabase Edge Function
 *
 * This function:
 * 1. Checks the cache first to avoid unnecessary API calls (only when no cart items provided)
 * 2. Calls a Supabase Edge Function to validate the code with Stripe
 * 3. Validates that the discount applies to items in the cart (if cart items provided)
 * 4. Parses the discount information from the response
 * 5. Caches valid codes for 5 minutes (only basic validation, not cart-specific)
 * 6. Handles errors gracefully with user-friendly messages
 *
 * @param code - The discount/promotion code to validate
 * @param cartItems - Optional cart items to validate the discount applies to
 * @returns Promise<DiscountValidationResult> - Validation result with discount info or error
 */
export async function validateDiscountCode(
  code: string,
  cartItems?: CartItemForValidation[]
): Promise<DiscountValidationResult> {
  // Normalize code to uppercase (Stripe promotion codes are case-sensitive, we standardize on uppercase)
  const normalizedCode = code.trim().toUpperCase();
  // Use lowercase for cache key for case-insensitive cache lookup
  const cacheKey = normalizedCode.toLowerCase();

  if (!normalizedCode) {
    return {
      isValid: false,
      error: 'Please enter a discount code',
    };
  }

  // Only use cache when NOT validating against specific cart items
  // (cart items may change, so we need fresh validation)
  if (!cartItems || cartItems.length === 0) {
    const cached = discountCache.get(cacheKey);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < CACHE_DURATION_MS) {
        console.log(`Using cached discount code: ${cacheKey}`);
        return {
          isValid: true,
          discount: cached.discount,
        };
      } else {
        // Cache expired, remove it
        discountCache.delete(cacheKey);
      }
    }
  }

  try {
    // Call Supabase Edge Function to validate with Stripe
    // Send uppercase - we standardize all promotion codes to uppercase
    // Include cart items for applies_to validation if provided
    const { data, error } = await supabase.functions.invoke('validate-promotion-code', {
      body: {
        code: normalizedCode,
        cartItems: cartItems?.map(item => ({ type: item.type, itemId: item.itemId })),
      },
    });

    if (error) {
      console.error('Error validating discount code:', error);
      
      // Handle specific error cases
      if (error.message?.includes('not found') || error.message?.includes('invalid')) {
        return {
          isValid: false,
          error: 'Invalid discount code. Please check and try again.',
        };
      }
      
      if (error.message?.includes('expired')) {
        return {
          isValid: false,
          error: 'This discount code has expired.',
        };
      }
      
      if (error.message?.includes('usage limit')) {
        return {
          isValid: false,
          error: 'This discount code has reached its usage limit.',
        };
      }

      if (error.message?.includes('not applicable')) {
        return {
          isValid: false,
          error: 'This discount code is not applicable to the items in your cart.',
        };
      }

      // Generic error
      return {
        isValid: false,
        error: 'Unable to validate discount code. Please try again.',
      };
    }

    if (!data || !data.isValid) {
      return {
        isValid: false,
        error: data?.error || 'Invalid discount code',
      };
    }

    // Parse discount information
    const discount: DiscountInfo = {
      code: normalizedCode,
      amountOff: data.amountOff,
      percentOff: data.percentOff,
      description: data.description,
      promotionCodeId: data.promotionCodeId,
    };

    // Cache the valid discount (using lowercase key for case-insensitive cache lookup)
    discountCache.set(cacheKey, {
      discount,
      timestamp: Date.now(),
    });

    return {
      isValid: true,
      discount,
    };
  } catch (err) {
    console.error('Unexpected error validating discount code:', err);
    
    // Handle network errors
    if (
      err instanceof Error &&
      (err.message.includes('Failed to fetch') ||
        err.message.includes('Network request failed') ||
        err.message.includes('NetworkError'))
    ) {
      return {
        isValid: false,
        error: 'Network error. Please check your connection and try again.',
      };
    }
    
    return {
      isValid: false,
      error: 'An unexpected error occurred. Please try again.',
    };
  }
}

/**
 * Calculate the discounted amount based on discount info
 * 
 * @param originalAmount - Original amount in cents
 * @param discount - Discount information
 * @returns Discounted amount in cents
 */
export function calculateDiscountedAmount(
  originalAmount: number,
  discount: DiscountInfo
): number {
  if (discount.amountOff) {
    // Fixed amount discount
    return Math.max(0, originalAmount - discount.amountOff);
  } else if (discount.percentOff) {
    // Percentage discount
    const discountAmount = Math.round((originalAmount * discount.percentOff) / 100);
    return Math.max(0, originalAmount - discountAmount);
  }
  
  // No discount applied
  return originalAmount;
}

/**
 * Get the discount amount (how much is being discounted)
 * 
 * @param originalAmount - Original amount in cents
 * @param discount - Discount information
 * @returns Discount amount in cents
 */
export function getDiscountAmount(
  originalAmount: number,
  discount: DiscountInfo
): number {
  if (discount.amountOff) {
    return Math.min(discount.amountOff, originalAmount);
  } else if (discount.percentOff) {
    return Math.round((originalAmount * discount.percentOff) / 100);
  }
  
  return 0;
}

/**
 * Clear the discount code cache
 * Useful for testing or when needed to force revalidation
 */
export function clearDiscountCache(): void {
  discountCache.clear();
}

/**
 * Format discount for display
 * 
 * @param discount - Discount information
 * @returns Formatted string (e.g., "$10 off" or "20% off")
 */
export function formatDiscount(discount: DiscountInfo): string {
  if (discount.amountOff) {
    return `$${(discount.amountOff / 100).toFixed(2)} off`;
  } else if (discount.percentOff) {
    return `${discount.percentOff}% off`;
  }
  
  return 'Discount applied';
}
